import React, { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import iconPresentActive from "@/assets/attendance/anwesend_aktiv.png";
import iconPresentInactive from "@/assets/attendance/anwesend_inaktiv.png";
import iconAbsentActive from "@/assets/attendance/fehlt_aktiv.png";
import iconAbsentInactive from "@/assets/attendance/fehlt_inaktiv.png";
import iconExcusedActive from "@/assets/attendance/entschuldigt_aktiv.png";
import iconExcusedInactive from "@/assets/attendance/entschuldigt_inaktiv.png";

export function DemoTab({ 
  children, 
  groups,
  sessions,
  attendance,
  authToken,
  API_BASE,
  isMobile 
}) {
  const [activeVariant, setActiveVariant] = useState("modern");
  const [activeSection, setActiveSection] = useState("list");
  const [groupFilter, setGroupFilter] = useState("all");
  const [attendanceGroupFilter, setAttendanceGroupFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [demoAttendance, setDemoAttendance] = useState({});
  const [selectedChild, setSelectedChild] = useState(null);
  const [profileTab, setProfileTab] = useState("info");

  // Sessions
  const sortedSessions = useMemo(() => [...(sessions || [])].sort((a, b) => new Date(b.date) - new Date(a.date)), [sessions]);
  const [selectedSessionIndex, setSelectedSessionIndex] = useState(0);
  const selectedSession = sortedSessions[selectedSessionIndex];

  // Gefilterte Kinder
  const filteredChildren = useMemo(() => {
    let list = children || [];
    if (!showArchived) list = list.filter((c) => c.status !== "archiviert");
    if (groupFilter !== "all") list = list.filter((c) => c.group_id === groupFilter);
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter((c) => `${c.first_name} ${c.last_name}`.toLowerCase().includes(term));
    }
    return list;
  }, [children, groupFilter, searchTerm, showArchived]);

  // Kinder für Anwesenheit
  const attendanceChildren = useMemo(() => {
    let list = (children || []).filter((c) => c.status !== "archiviert");
    if (attendanceGroupFilter && attendanceGroupFilter !== "all") {
      list = list.filter((c) => c.group_id === attendanceGroupFilter);
    }
    return list;
  }, [children, attendanceGroupFilter]);

  // Helpers
  const getGroupStyle = (groupId) => {
    const group = groups?.find((g) => g.id === groupId);
    if (!group) return { bg: "#e5e7eb", text: "#1f2937" };
    const bg = group.color || "#e5e7eb";
    let textColor = "#1f2937";
    try {
      if (bg.startsWith("#") && bg.length === 7) {
        const r = parseInt(bg.slice(1, 3), 16);
        const g = parseInt(bg.slice(3, 5), 16);
        const b = parseInt(bg.slice(5, 7), 16);
        textColor = (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? "#1f2937" : "#ffffff";
      }
    } catch (e) {}
    return { bg, text: textColor };
  };

  const getGroupLabel = (groupId) => {
    const group = groups?.find((g) => g.id === groupId);
    if (!group) return "–";
    return `${group.name}${group.start_time ? ` · ${group.start_time.slice(0, 5)}` : ""}`;
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return "";
    const [y, m, d] = isoStr.split("-");
    return `${d}.${m}.${y}`;
  };

  const toggleDemoAttendance = (childId, newStatus) => {
    setDemoAttendance((prev) => {
      if (prev[childId] === newStatus) {
        const { [childId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [childId]: newStatus };
    });
  };

  const getGroupAttendanceCount = (groupId) => {
    const groupChildren = (children || []).filter((c) => c.group_id === groupId && c.status !== "archiviert");
    return groupChildren.filter((c) => demoAttendance[c.id] === "present").length;
  };

  // Wenn ein Kind ausgewählt ist, zeige Profil
  if (selectedChild) {
    return (
      <ChildProfileDemo
        child={selectedChild}
        groups={groups}
        sessions={sessions}
        attendance={attendance}
        authToken={authToken}
        API_BASE={API_BASE}
        onBack={() => setSelectedChild(null)}
        getGroupStyle={getGroupStyle}
        getGroupLabel={getGroupLabel}
        formatDate={formatDate}
        profileTab={profileTab}
        setProfileTab={setProfileTab}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Varianten-Auswahl */}
      <div className="rounded-xl bg-white p-4 shadow-sm">
        <div className="mb-2 text-sm font-semibold text-slate-900">Design-Varianten Demo</div>
        <p className="mb-3 text-xs text-slate-500">Klicke auf ein Kind, um das neue Profil-Design zu sehen</p>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant={activeVariant === "current" ? "default" : "outline"} onClick={() => setActiveVariant("current")}>
            Aktuell
          </Button>
          <Button
            size="sm"
            variant={activeVariant === "modern" ? "default" : "outline"}
            onClick={() => setActiveVariant("modern")}
            className={activeVariant === "modern" ? "bg-violet-600 hover:bg-violet-500" : ""}
          >
            Modern Minimal ✨
          </Button>
        </div>
      </div>

      {activeVariant === "modern" && (
        <div className="space-y-3">
          {/* Segment Control */}
          <div className="flex rounded-lg bg-slate-100 p-1">
            {["list", "attendance", "stats"].map((section) => (
              <button
                key={section}
                onClick={() => setActiveSection(section)}
                className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-all ${
                  activeSection === section ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"
                }`}
              >
                {section === "list" ? "Kinderliste" : section === "attendance" ? "Anwesenheit" : "Statistik"}
              </button>
            ))}
          </div>

          {/* KINDERLISTE */}
          {activeSection === "list" && (
            <>
              <div className="relative">
                <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Kind suchen..."
                  className="w-full rounded-xl border-0 bg-slate-100 py-2.5 pl-10 pr-4 text-sm placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setGroupFilter("all")}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium transition-all ${
                    groupFilter === "all" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  Alle ({children?.filter(c => c.status !== "archiviert").length || 0})
                </button>
                {groups?.map((g) => {
                  const style = getGroupStyle(g.id);
                  const count = children?.filter(c => c.group_id === g.id && c.status !== "archiviert").length || 0;
                  return (
                    <button
                      key={g.id}
                      onClick={() => setGroupFilter(g.id)}
                      className={`rounded-full px-3 py-1.5 text-xs font-medium ${groupFilter === g.id ? "ring-2 ring-offset-1 ring-slate-400" : ""}`}
                      style={{ backgroundColor: style.bg, color: style.text }}
                    >
                      {g.name} · {g.start_time?.slice(0, 5)} ({count})
                    </button>
                  );
                })}
              </div>

              <div className="text-xs text-slate-500">{filteredChildren.length} Kind{filteredChildren.length !== 1 ? "er" : ""}</div>

              <div className="space-y-2">
                {filteredChildren.slice(0, 12).map((child) => {
                  const groupStyle = getGroupStyle(child.group_id);
                  const initials = `${child.first_name?.[0] || ""}${child.last_name?.[0] || ""}`;
                  return (
                    <div
                      key={child.id}
                      onClick={() => setSelectedChild(child)}
                      className="flex cursor-pointer items-center justify-between rounded-xl bg-white p-3 shadow-sm transition-all hover:shadow-md"
                    >
                      <div className="flex items-center gap-3">
                        {child.has_photo ? (
                          <img src={`${API_BASE}/api/children/${child.id}/photo?token=${authToken}`} alt="" className="h-11 w-11 rounded-full object-cover" />
                        ) : (
                          <div className="flex h-11 w-11 items-center justify-center rounded-full text-sm font-semibold" style={{ backgroundColor: groupStyle.bg, color: groupStyle.text }}>
                            {initials}
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-slate-900">{child.first_name} {child.last_name}</div>
                          <div className="text-xs text-slate-500">{getGroupLabel(child.group_id)}</div>
                        </div>
                      </div>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ANWESENHEIT */}
          {activeSection === "attendance" && (
            <div className="space-y-3">
              {sortedSessions.length > 0 && (
                <div className="flex items-center justify-between rounded-xl bg-white p-3 shadow-sm">
                  <button onClick={() => setSelectedSessionIndex((i) => Math.min(i + 1, sortedSessions.length - 1))} disabled={selectedSessionIndex >= sortedSessions.length - 1} className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-30">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  </button>
                  <div className="text-center">
                    <div className="text-sm font-semibold text-slate-900">{selectedSession ? formatDate(selectedSession.date) : "–"}</div>
                    <div className="text-xs text-slate-500">{selectedSession?.weekday || ""}</div>
                  </div>
                  <button onClick={() => setSelectedSessionIndex((i) => Math.max(i - 1, 0))} disabled={selectedSessionIndex <= 0} className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-30">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </button>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {groups?.map((g) => {
                  const style = getGroupStyle(g.id);
                  const presentCount = getGroupAttendanceCount(g.id);
                  const totalCount = children?.filter(c => c.group_id === g.id && c.status !== "archiviert").length || 0;
                  return (
                    <button
                      key={g.id}
                      onClick={() => setAttendanceGroupFilter(attendanceGroupFilter === g.id ? null : g.id)}
                      className={`rounded-full px-3 py-1.5 text-xs font-medium ${attendanceGroupFilter === g.id ? "ring-2 ring-offset-1 ring-slate-400" : ""}`}
                      style={{ backgroundColor: style.bg, color: style.text }}
                    >
                      {g.name} · {g.start_time?.slice(0, 5)} ({presentCount}/{totalCount})
                    </button>
                  );
                })}
              </div>

              {!attendanceGroupFilter && <p className="text-xs text-slate-500">Bitte eine Gruppe auswählen.</p>}

              {attendanceGroupFilter && (
                <div className="space-y-2">
                  {attendanceChildren.map((child) => {
                    const currentStatus = demoAttendance[child.id] || null;
                    const groupStyle = getGroupStyle(child.group_id);
                    const initials = `${child.first_name?.[0] || ""}${child.last_name?.[0] || ""}`;
                    return (
                      <div key={child.id} className="flex items-center justify-between rounded-xl bg-white p-3 shadow-sm">
                        <div className="flex items-center gap-3">
                          {child.has_photo ? (
                            <img src={`${API_BASE}/api/children/${child.id}/photo?token=${authToken}`} alt="" className="h-10 w-10 rounded-full object-cover" />
                          ) : (
                            <div className="flex h-10 w-10 items-center justify-center rounded-full text-xs font-semibold" style={{ backgroundColor: groupStyle.bg, color: groupStyle.text }}>{initials}</div>
                          )}
                          <div className="font-medium text-slate-900">{child.first_name} {child.last_name}</div>
                        </div>
                        <div className="flex items-center gap-1">
                          {[{ status: "present", icon: currentStatus === "present" ? iconPresentActive : iconPresentInactive, ring: "ring-emerald-400", bg: "bg-emerald-50" },
                            { status: "absent", icon: currentStatus === "absent" ? iconAbsentActive : iconAbsentInactive, ring: "ring-rose-400", bg: "bg-rose-50" },
                            { status: "excused", icon: currentStatus === "excused" ? iconExcusedActive : iconExcusedInactive, ring: "ring-amber-400", bg: "bg-amber-50" }
                          ].map(({ status, icon, ring, bg }) => (
                            <button
                              key={status}
                              onClick={() => toggleDemoAttendance(child.id, status)}
                              className={`flex h-10 w-10 items-center justify-center rounded-lg transition-all ${currentStatus === status ? `${bg} ring-2 ${ring}` : "hover:bg-slate-50"}`}
                            >
                              <img src={icon} alt="" className="h-7 w-7" />
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* STATISTIK */}
          {activeSection === "stats" && (
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-xl bg-white p-4 shadow-sm">
                  <div className="text-2xl font-bold text-slate-900">{children?.filter(c => c.status === "aktiv").length || 0}</div>
                  <div className="text-xs text-slate-500">Aktive Kinder</div>
                </div>
                <div className="rounded-xl bg-white p-4 shadow-sm">
                  <div className="text-2xl font-bold text-amber-600">{children?.filter(c => c.status === "pause").length || 0}</div>
                  <div className="text-xs text-slate-500">In Pause</div>
                </div>
                <div className="rounded-xl bg-white p-4 shadow-sm">
                  <div className="text-2xl font-bold text-slate-400">{children?.filter(c => c.status === "archiviert").length || 0}</div>
                  <div className="text-xs text-slate-500">Archiviert</div>
                </div>
              </div>

              <div className="rounded-xl bg-white p-4 shadow-sm">
                <div className="mb-3 text-sm font-semibold text-slate-900">Kinder pro Gruppe</div>
                {groups?.map((g) => {
                  const count = children?.filter(c => c.group_id === g.id && c.status !== "archiviert").length || 0;
                  const maxCount = Math.max(...(groups?.map(gr => children?.filter(c => c.group_id === gr.id && c.status !== "archiviert").length || 0) || [1]));
                  const style = getGroupStyle(g.id);
                  return (
                    <div key={g.id} className="mb-2">
                      <div className="mb-1 flex justify-between text-xs">
                        <span className="font-medium">{g.name}</span>
                        <span className="text-slate-500">{count}</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100">
                        <div className="h-full rounded-full" style={{ width: `${maxCount > 0 ? (count / maxCount) * 100 : 0}%`, backgroundColor: style.bg }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {activeVariant === "current" && (
        <div className="rounded-xl border border-slate-200 bg-white/90 p-4">
          <p className="text-sm text-slate-500">Wechsle zu "Modern Minimal" um das neue Design zu sehen.</p>
        </div>
      )}
    </div>
  );
}

// =====================================================
// KINDERPROFIL DEMO KOMPONENTE
// =====================================================
function ChildProfileDemo({ child, groups, sessions, attendance, authToken, API_BASE, onBack, getGroupStyle, getGroupLabel, formatDate, profileTab, setProfileTab }) {
  const groupStyle = getGroupStyle(child.group_id);
  const initials = `${child.first_name?.[0] || ""}${child.last_name?.[0] || ""}`;

  // Seepferdchen Training
  const seepferdchen = child.training_seepferdchen || {};
  const seepferdchenItems = [
    { key: "tauchen", label: "Tauchen" },
    { key: "springen", label: "Springen" },
    { key: "gleiten", label: "Gleiten" },
    { key: "sprung_ins_wasser", label: "Sprung ins Wasser" },
    { key: "schwimmen_25m", label: "25m Schwimmen" },
  ];

  // Freischwimmer Training
  const freischwimmer = child.training_freischwimmer || {};
  const freischwimmerItems = [
    { key: "schwimmen", label: "Schwimmen" },
    { key: "tauchen", label: "Tauchen" },
    { key: "springen", label: "Springen" },
    { key: "gleiten", label: "Gleiten" },
    { key: "gesicht_im_wasser", label: "Gesicht im Wasser" },
    { key: "sprung_ins_wasser", label: "Sprung ins Wasser" },
    { key: "baderegeln", label: "Baderegeln" },
    { key: "sicher_im_wasser", label: "Sicher im Wasser" },
    { key: "rueckenschwimmen", label: "Rückenschwimmen" },
    { key: "toter_mann", label: '"toter Mann"' },
  ];

  // Anwesenheits-Statistik (Demo-Daten)
  const attendanceStats = useMemo(() => {
    // Simulierte Statistik basierend auf Sessions
    const totalSessions = sessions?.length || 0;
    const presentCount = Math.floor(totalSessions * 0.75);
    const absentCount = Math.floor(totalSessions * 0.15);
    const excusedCount = totalSessions - presentCount - absentCount;
    return { total: totalSessions, present: presentCount, absent: absentCount, excused: excusedCount };
  }, [sessions]);

  return (
    <div className="flex flex-col gap-4">
      {/* Header mit Zurück-Button */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm hover:bg-slate-50"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="text-lg font-semibold text-slate-900">Profil</div>
      </div>

      {/* Profil-Header */}
      <div className="rounded-2xl bg-white p-6 shadow-sm">
        <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
          {/* Avatar */}
          {child.has_photo ? (
            <img
              src={`${API_BASE}/api/children/${child.id}/photo?token=${authToken}`}
              alt={`${child.first_name} ${child.last_name}`}
              className="h-24 w-24 rounded-2xl object-cover shadow-md"
            />
          ) : (
            <div
              className="flex h-24 w-24 items-center justify-center rounded-2xl text-2xl font-bold shadow-md"
              style={{ backgroundColor: groupStyle.bg, color: groupStyle.text }}
            >
              {initials}
            </div>
          )}

          {/* Info */}
          <div className="flex-1 text-center sm:text-left">
            <h2 className="text-xl font-bold text-slate-900">{child.first_name} {child.last_name}</h2>
            <div className="mt-1 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
              <span
                className="inline-flex rounded-full px-3 py-1 text-xs font-medium"
                style={{ backgroundColor: groupStyle.bg, color: groupStyle.text }}
              >
                {getGroupLabel(child.group_id)}
              </span>
              <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                child.status === "aktiv" ? "bg-emerald-100 text-emerald-700" :
                child.status === "pause" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"
              }`}>
                {child.status === "aktiv" ? "Aktiv" : child.status === "pause" ? "Pause" : child.status}
              </span>
            </div>
            {child.pause_until && (
              <p className="mt-2 text-sm text-amber-600">Pause bis {formatDate(child.pause_until)}</p>
            )}
          </div>
        </div>
      </div>

      {/* Tab-Navigation */}
      <div className="flex rounded-lg bg-slate-100 p-1">
        {[
          { key: "info", label: "Info" },
          { key: "training", label: "Leistungsstand" },
          { key: "attendance", label: "Anwesenheit" },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setProfileTab(tab.key)}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-all ${
              profileTab === tab.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* INFO TAB */}
      {profileTab === "info" && (
        <div className="space-y-3">
          <div className="rounded-xl bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-slate-900">Kontaktdaten</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">E-Mail</span>
                <span className="font-medium">{child.email || "–"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Telefon</span>
                <span className="font-medium">{child.phone || "–"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Geburtstag</span>
                <span className="font-medium">{child.birth_date ? formatDate(child.birth_date) : "–"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Geschlecht</span>
                <span className="font-medium">{child.gender === "junge" ? "Junge" : child.gender === "maedchen" ? "Mädchen" : "–"}</span>
              </div>
            </div>
          </div>

          {child.notes && (
            <div className="rounded-xl bg-white p-4 shadow-sm">
              <h3 className="mb-2 text-sm font-semibold text-slate-900">Notizen</h3>
              <p className="text-sm text-slate-600">{child.notes}</p>
            </div>
          )}

          <div className="flex gap-2">
            <button className="flex-1 rounded-xl bg-violet-600 py-3 text-sm font-medium text-white shadow-sm hover:bg-violet-500">
              Bearbeiten
            </button>
            <button className="flex-1 rounded-xl bg-slate-100 py-3 text-sm font-medium text-slate-700 hover:bg-slate-200">
              PDF exportieren
            </button>
          </div>
        </div>
      )}

      {/* LEISTUNGSSTAND TAB */}
      {profileTab === "training" && (
        <div className="space-y-3">
          {/* Seepferdchen */}
          <div className="rounded-xl bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900">Seepferdchen</h3>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${seepferdchen.zeugnis ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {seepferdchen.zeugnis ? "Zeugnis erhalten" : "In Ausbildung"}
              </span>
            </div>
            <div className="space-y-2">
              {seepferdchenItems.map(({ key, label }) => {
                const value = seepferdchen[key] ?? 0;
                return (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">{label}</span>
                    <div className="flex gap-1">
                      {[1, 2, 3].map((n) => (
                        <div
                          key={n}
                          className={`h-6 w-6 rounded-full text-center text-xs font-semibold leading-6 ${
                            n <= value ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400"
                          }`}
                        >
                          {n}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Freischwimmer */}
          <div className="rounded-xl bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900">Freischwimmer</h3>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${freischwimmer.zeugnis ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {freischwimmer.zeugnis ? "Zeugnis erhalten" : "In Ausbildung"}
              </span>
            </div>
            <div className="space-y-2">
              {freischwimmerItems.map(({ key, label }) => {
                const value = freischwimmer[key] ?? 0;
                return (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">{label}</span>
                    <div className="flex gap-1">
                      {[1, 2, 3].map((n) => (
                        <div
                          key={n}
                          className={`h-6 w-6 rounded-full text-center text-xs font-semibold leading-6 ${
                            n <= value ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400"
                          }`}
                        >
                          {n}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ANWESENHEIT TAB */}
      {profileTab === "attendance" && (
        <div className="space-y-3">
          {/* Statistik-Übersicht */}
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-xl bg-emerald-50 p-3 text-center">
              <div className="text-xl font-bold text-emerald-600">{attendanceStats.present}</div>
              <div className="text-[10px] text-emerald-600">Anwesend</div>
            </div>
            <div className="rounded-xl bg-rose-50 p-3 text-center">
              <div className="text-xl font-bold text-rose-600">{attendanceStats.absent}</div>
              <div className="text-[10px] text-rose-600">Gefehlt</div>
            </div>
            <div className="rounded-xl bg-amber-50 p-3 text-center">
              <div className="text-xl font-bold text-amber-600">{attendanceStats.excused}</div>
              <div className="text-[10px] text-amber-600">Entschuldigt</div>
            </div>
          </div>

          {/* Quote */}
          <div className="rounded-xl bg-white p-4 shadow-sm">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-900">Anwesenheitsquote</span>
              <span className="text-lg font-bold text-emerald-600">
                {attendanceStats.total > 0 ? Math.round((attendanceStats.present / attendanceStats.total) * 100) : 0}%
              </span>
            </div>
            <div className="h-3 overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-emerald-500 transition-all"
                style={{ width: `${attendanceStats.total > 0 ? (attendanceStats.present / attendanceStats.total) * 100 : 0}%` }}
              />
            </div>
            <p className="mt-2 text-xs text-slate-500">
              {attendanceStats.present} von {attendanceStats.total} Terminen anwesend
            </p>
          </div>

          {/* Letzte Termine */}
          <div className="rounded-xl bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-slate-900">Letzte Termine</h3>
            <div className="space-y-2">
              {sessions?.slice(0, 5).map((session, i) => {
                const statuses = ["present", "present", "absent", "present", "excused"];
                const status = statuses[i % 5];
                return (
                  <div key={session.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                    <div>
                      <div className="text-sm font-medium">{formatDate(session.date)}</div>
                      <div className="text-xs text-slate-500">{session.weekday}</div>
                    </div>
                    <img
                      src={status === "present" ? iconPresentActive : status === "absent" ? iconAbsentActive : iconExcusedActive}
                      alt=""
                      className="h-6 w-6"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
